<template>
  <div class="system-page archive-page">
    <section v-if="!detail" class="panel">
      <div class="panel-title">归档查询</div>
      <p class="panel-note">查询正向生成的结算清单。点开一条可看合并明细；文件还在时可下载。</p>

      <div class="toolbar">
        <Input
          v-model="keyword"
          placeholder="搜索项目名称 / 项目定义号 / 文件名"
          style="width: 320px"
          @keyup.enter="search"
        />
        <Button color="primary" @click="search">查询</Button>
        <Button @click="resetSearch">重置</Button>
      </div>

      <div v-if="loading" class="empty-hint">加载中…</div>
      <div v-else-if="!items.length" class="empty-hint">没有匹配的归档记录。</div>
      <div v-else class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>生成时间</th>
              <th>项目名称</th>
              <th>项目定义号</th>
              <th>来源文件</th>
              <th>原始 / 合并</th>
              <th>操作人</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.id" class="is-clickable" @click="openDetail(item.id)">
              <td>{{ item.createdAt }}</td>
              <td>{{ item.projectName }}</td>
              <td>{{ item.wbsCode }}</td>
              <td>{{ item.sourceName || '—' }}</td>
              <td>{{ item.rawRowCount }} / {{ item.mergedCount }}</td>
              <td>{{ item.createdBy || '—' }}</td>
              <td class="col-actions" @click.stop>
                <Button size="s" @click="openDetail(item.id)">明细</Button>
                <a
                  v-if="item.fileExists && item.downloadUrl"
                  class="link-btn"
                  :href="item.downloadUrl"
                  :download="item.filename"
                >下载</a>
                <span v-else class="status-muted">已清理</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="total > pageSize" class="pager">
        <Button size="s" :disabled="page <= 1" @click="changePage(page - 1)">上一页</Button>
        <span>{{ page }} / {{ pageCount }} · 共 {{ total }} 条</span>
        <Button size="s" :disabled="page >= pageCount" @click="changePage(page + 1)">下一页</Button>
      </div>
    </section>

    <template v-else>
      <section class="panel">
        <div class="detail-head">
          <Button size="s" @click="backToList">返回列表</Button>
          <div class="panel-title detail-title">{{ detail.projectName || '结算清单明细' }}</div>
          <a
            v-if="detail.fileExists && detail.downloadUrl"
            class="link-btn"
            :href="detail.downloadUrl"
            :download="detail.filename"
          >下载结算清单</a>
          <span v-else class="status-muted">Excel 已清理，仍可查看库中明细</span>
        </div>

        <div class="info-grid">
          <div class="info-item">
            <span class="label">项目定义号</span>
            <span class="value">{{ detail.wbsCode }}</span>
          </div>
          <div class="info-item">
            <span class="label">工作表</span>
            <span class="value">{{ detail.sheetName || '—' }}</span>
          </div>
          <div class="info-item">
            <span class="label">来源文件</span>
            <span class="value">{{ detail.sourceName || '—' }}</span>
          </div>
          <div class="info-item">
            <span class="label">生成时间</span>
            <span class="value">{{ detail.createdAt }}</span>
          </div>
          <div class="info-item">
            <span class="label">操作人</span>
            <span class="value">{{ detail.createdBy || '—' }}</span>
          </div>
          <div class="info-item">
            <span class="label">原始 / 合并</span>
            <span class="value">{{ detail.rawRowCount }} / {{ detail.mergedCount }}</span>
          </div>
          <div class="info-item">
            <span class="label">数量合计</span>
            <span class="value">{{ formatNum(detail.qtyTotal) }}</span>
          </div>
          <div class="info-item">
            <span class="label">金额合计（不含税）</span>
            <span class="value">{{ formatNum(detail.amountExclTotal) }}</span>
          </div>
        </div>
      </section>

      <section class="panel">
        <div class="toolbar">
          <span class="sub-title">合并明细</span>
          <Input
            v-model="lineKeyword"
            placeholder="筛选物料编码 / 描述 / 批次"
            style="width: 280px"
          />
        </div>
        <div v-if="!filteredLines.length" class="empty-hint">没有匹配的明细行。</div>
        <div v-else class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>序号</th>
                <th>物料编码</th>
                <th>物料描述</th>
                <th>单位</th>
                <th>数量</th>
                <th>批次</th>
                <th>单价</th>
                <th>金额</th>
                <th>含税金额</th>
                <th>备注</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line in filteredLines" :key="line.seq + '-' + line.materialCode">
                <td>{{ line.seq }}</td>
                <td>{{ line.materialCode }}</td>
                <td class="cell-wrap">{{ line.description }}</td>
                <td>{{ line.unit }}</td>
                <td>{{ formatNum(line.qty) }}</td>
                <td>{{ line.batch }}</td>
                <td>{{ formatNum(line.unitPrice) }}</td>
                <td>{{ formatNum(line.amountExcl) }}</td>
                <td>{{ formatNum(line.amountIncl) }}</td>
                <td class="cell-wrap">{{ line.remark || '' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { message } from 'heyui';
import { settlementProcessApi as api } from '@/api/settlementProcess';

const PAGE_SIZE = 20;

const route = useRoute();
const router = useRouter();

const loading = ref(false);
const keyword = ref('');
const page = ref(1);
const total = ref(0);
const items = ref([]);
const detail = ref(null);
const lineKeyword = ref('');

const pageSize = PAGE_SIZE;
const pageCount = computed(() => Math.max(1, Math.ceil(total.value / PAGE_SIZE)));

const filteredLines = computed(() => {
  const lines = detail.value?.lines || [];
  const q = lineKeyword.value.trim().toLowerCase();
  if (!q) return lines;
  return lines.filter((line) => {
    const blob = `${line.materialCode || ''} ${line.description || ''} ${line.batch || ''}`.toLowerCase();
    return blob.includes(q);
  });
});

function formatNum(value) {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString('zh-CN', { maximumFractionDigits: 4 });
}

async function loadList() {
  loading.value = true;
  try {
    const offset = (page.value - 1) * PAGE_SIZE;
    const resp = await api.listRecords(keyword.value.trim(), PAGE_SIZE, offset);
    if (!resp.ok) {
      message.error(resp.error || '查询失败');
      return;
    }
    items.value = resp.items || [];
    total.value = resp.total || 0;
  } finally {
    loading.value = false;
  }
}

async function loadDetail(id) {
  loading.value = true;
  detail.value = null;
  lineKeyword.value = '';
  try {
    const resp = await api.getRecord(id);
    if (!resp.ok) {
      message.error(resp.error || '记录不存在');
      router.replace({ name: 'SettlementArchive' });
      return;
    }
    detail.value = resp;
  } finally {
    loading.value = false;
  }
}

function search() {
  page.value = 1;
  loadList();
}

function resetSearch() {
  keyword.value = '';
  page.value = 1;
  loadList();
}

function changePage(next) {
  page.value = next;
  loadList();
}

function openDetail(id) {
  router.push({ name: 'SettlementArchiveDetail', params: { id: String(id) } });
}

function backToList() {
  router.push({ name: 'SettlementArchive' });
}

function syncFromRoute() {
  const id = route.params.id;
  if (id) {
    loadDetail(id);
    return;
  }
  detail.value = null;
  loadList();
}

onMounted(syncFromRoute);
watch(
  () => route.fullPath,
  () => {
    syncFromRoute();
  },
);
</script>

<style lang="less" scoped>
@import './system-pages.less';

.archive-page {
  max-width: 1200px;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin: 4px 0 16px;
}

.sub-title {
  font-size: 14px;
  font-weight: 600;
  color: @app-text;
  margin-right: 8px;
}

.empty-hint {
  padding: 28px 0;
  text-align: center;
  color: @app-muted;
  font-size: 13px;
}

.table-wrap {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th, td {
    padding: 9px 10px;
    text-align: left;
    border-bottom: 1px solid #eef1f5;
    white-space: nowrap;
  }

  th {
    color: @app-muted;
    font-weight: 500;
  }

  td {
    color: @app-text;
  }

  .is-clickable {
    cursor: pointer;

    &:hover {
      background: #f7f9fc;
    }
  }
}

.cell-wrap {
  white-space: normal !important;
  max-width: 280px;
}

.col-actions {
  white-space: nowrap;
}

.status-ok {
  color: @app-primary;
}

.status-muted {
  color: @app-muted;
  font-size: 13px;
}

.link-btn {
  margin-left: 8px;
  color: @app-primary;
  text-decoration: none;
  font-size: 13px;

  &:hover {
    text-decoration: underline;
  }
}

.pager {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
  font-size: 13px;
  color: @app-muted;
}

.detail-head {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.detail-title {
  margin: 0;
  flex: 1;
}
</style>
